/**
 * Marques — the one list of brands, and what carries each.
 *
 * Articles carry a brand as text; the list is where that text is spelled once.
 * It is what the brand filter offers and what an article's brand is picked
 * from, so the page answers three questions a person managing it asks:
 * *which brands do we have, how many articles carry each, and is anything in
 * use that the list has not met?*
 *
 * **Renaming renames the articles.** The list is the source of truth for the
 * spelling, and an article left on the old one would no longer match it — so
 * the dialog says so before it is confirmed. **Hiding does not touch articles**:
 * a hidden brand is no longer offered for new assignments, and the articles that
 * carry it keep it (and the filter still finds them).
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useDebounced, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { quantity } from "@hardauto/core-i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  Input,
  Page,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { CatalogApi, type Brand } from "../api";
import { NAMESPACE } from "../i18n";
import "./catalog.css";

export function BrandsScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const api = useMemo(() => new CatalogApi(client), [client]);
  const mayWrite = useCan("catalog.category.write");

  const fetcher = useCallback(() => api.brands(true), [api]);
  const brands = useResource(fetcher);

  const [term, setTerm] = useState("");
  const search = useDebounced(term, 200);
  const [label, setLabel] = useState("");
  const [renaming, setRenaming] = useState<Brand | null>(null);
  const [draft, setDraft] = useState("");

  const items = brands.data?.items ?? [];
  const missing = brands.data?.missing ?? [];
  const needle = search.trim().toLocaleLowerCase(i18n.language);
  const shown = needle
    ? items.filter((brand) => brand.label.toLocaleLowerCase(i18n.language).includes(needle))
    : items;

  function failed(error: unknown) {
    toast.show(error instanceof ApiError ? error.message : t("error.title"), { tone: "danger" });
  }

  const columns: Column<Brand>[] = [
    {
      key: "label",
      header: t("brandsPage.column.name"),
      render: (brand) => (
        <span>
          {brand.label}{" "}
          {brand.is_active ? null : <Badge tone="neutral">{t("categories.inactive")}</Badge>}
        </span>
      ),
    },
    {
      key: "articles",
      header: t("brandsPage.column.articles"),
      align: "end",
      render: (brand) => (
        <span className="hardauto-num">{quantity(String(brand.article_count), i18n.language)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      render: (brand) =>
        mayWrite ? (
          <span className="hardauto-brands__actions">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setRenaming(brand);
                setDraft(brand.label);
              }}
            >
              {t("brandsPage.rename")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              busy={action.isBusy(brand.id)}
              onClick={() => {
                void action.run(brand.id, async () => {
                  try {
                    await api.updateBrand(brand.id, { is_active: !brand.is_active });
                    brands.reload();
                  } catch (error) {
                    failed(error);
                  }
                });
              }}
            >
              {brand.is_active ? t("categories.deactivate") : t("categories.activate")}
            </Button>
          </span>
        ) : null,
    },
  ];

  const cleaned = draft.trim();
  const canRename = renaming !== null && cleaned !== "" && cleaned !== renaming.label;

  return (
    <Page
      title={t("brandsPage.title")}
      description={t("brandsPage.description")}
      eyebrow={brands.data ? t("brandsPage.count", { count: items.length }) : undefined}
    >
      {mayWrite && missing.length > 0 ? (
        <Alert
          tone="warning"
          title={t("brandsPage.missingTitle", { count: missing.length })}
          action={
            <Button
              size="sm"
              variant="primary"
              busy={action.isBusy("sync")}
              onClick={() => {
                void action.run("sync", async () => {
                  try {
                    const result = await api.syncBrands();
                    toast.show(t("brandsPage.synced", { count: result.created }), {
                      tone: "success",
                    });
                    brands.reload();
                  } catch (error) {
                    failed(error);
                  }
                });
              }}
            >
              {t("brandsPage.addMissing")}
            </Button>
          }
        >
          {missing.slice(0, 12).join(", ")}
          {missing.length > 12 ? ` … +${missing.length - 12}` : ""}
        </Alert>
      ) : null}

      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("brandsPage.searchLabel")}
          placeholder={t("brandsPage.searchPlaceholder")}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
      </Toolbar>

      <DataTable<Brand>
        caption={t("brandsPage.caption")}
        columns={columns}
        rows={shown}
        rowKey={(brand) => brand.id}
        loading={brands.loading}
        loadingLabel={t("brandsPage.loading")}
        error={
          brands.failed
            ? { title: t("error.title"), onRetry: brands.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{ title: t("brandsPage.empty.title"), description: t("brandsPage.empty.body") }}
      />

      {mayWrite ? (
        <Card title={t("brandsPage.newTitle")} compactTitle>
          <form
            className="hardauto-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!label.trim()) return;
              void action.run("create", async () => {
                try {
                  await api.createBrand(label.trim());
                  setLabel("");
                  toast.show(t("brands.created"), { tone: "success" });
                  brands.reload();
                } catch (error) {
                  failed(error);
                }
              });
            }}
          >
            <div className="hardauto-inline-form__field">
              <Field label={t("brands.new")}>
                {(props) => (
                  <Input
                    {...props}
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="hardauto-inline-form__actions">
              <Button type="submit" variant="primary" busy={action.isBusy("create")}>
                {t("brands.add")}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Dialog
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        title={t("brandsPage.renameTitle", { brand: renaming?.label ?? "" })}
        description={t("brandsPage.renameWarning", { count: renaming?.article_count ?? 0 })}
        closeLabel={t("brandsPage.close")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenaming(null)}>
              {t("brandsPage.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={!canRename}
              busy={action.isBusy("rename")}
              onClick={() => {
                if (!renaming) return;
                void action.run("rename", async () => {
                  try {
                    await api.updateBrand(renaming.id, { label: cleaned });
                    toast.show(t("brandsPage.renamed", { count: renaming.article_count }), {
                      tone: "success",
                    });
                    setRenaming(null);
                    brands.reload();
                  } catch (error) {
                    failed(error);
                  }
                });
              }}
            >
              {t("brandsPage.renameConfirm")}
            </Button>
          </>
        }
      >
        <Field label={t("brandsPage.column.name")} required>
          {(props) => (
            <Input
              {...props}
              value={draft}
              maxLength={120}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
            />
          )}
        </Field>
      </Dialog>
    </Page>
  );
}
